/**
 * UI Components - Moon Moon Agency
 * Handles read more functionality, loader states, and UI components
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	toggle(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present
			if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 */
	init() {
		const splideElements = window.getElements('.splide');
		if (!splideElements.length || typeof Splide === 'undefined') return;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		splideElements.forEach(element => {
			// Skip already initialized sliders
			if (element._splide) return;

			// Skip product media sliders - they're synced and initialized in mm-product-medias.liquid
			const isProductMediaSlider = element.hasAttribute('data-main-slider') ||
										element.hasAttribute('data-thumbnail-slider');
			if (isProductMediaSlider) return;

			try {
				const configData = element.dataset.splide;
				let splideInstance;

				if (configData) {
					try {
						const config = JSON.parse(configData);
						splideInstance = new Splide(element, config).mount();
					} catch (jsonError) {
						window.log('warn', 'SplideManager', `Invalid JSON in data-splide, using defaults: ${element.className}`);
						splideInstance = new Splide(element).mount();
					}
				} else {
					// Configuration par défaut si pas de data-splide
					splideInstance = new Splide(element).mount();
				}
				element._splide = splideInstance;

				// L'autoplay Splide tourne sur une boucle requestAnimationFrame permanente, meme slider cache.
				// Avec un panneau taille (et son slider reassurance) par carte produit, ca fait des dizaines
				// de boucles en continu : on ne joue l'autoplay que lorsque le slider est visible.
				if (splideInstance.options.autoplay) {
					SplideManager.observeAutoplay(element);
				}

				// Avec autoWidth + trimSpace: "move", Splide ne desactive la fleche "next"
				// qu'au clic SUIVANT celui qui atteint visuellement la fin (son calcul se base
				// sur l'index du dernier slide, pas sur la position de scroll reelle).
				// On corrige l'etat "disabled" en comparant la position de scroll reelle
				// a la limite max, immediatement apres chaque deplacement.
				if (element.dataset.splide && element.dataset.splide.includes('autoWidth')) {
					const syncArrowsState = () => {
						const Move = splideInstance.Components.Move;
						const Controller = splideInstance.Components.Controller;
						const nextArrow = element.querySelector('.splide__arrow--next');
						if (!Move || !Controller || !nextArrow) return;

						const atEnd = Math.abs(Move.getPosition() - Move.getLimit(true)) < 1;
						nextArrow.disabled = atEnd;
					};
					splideInstance.on('mounted moved resized', syncArrowsState);
				}

				// Gérer la progress bar si elle existe
				const progressBar = element.querySelector('.splide__progress__bar');
				if (progressBar && splideInstance) {
					const updateProgress = function() {
						try {
							const end = splideInstance.Components.Controller.getEnd() + 1;
							const rate = Math.min((splideInstance.index + 1) / end, 1);
							progressBar.style.width = String(100 * rate) + '%';
						} catch (error) {
							window.log('error', 'SplideManager', 'Progress bar update failed');
						}
					};

					// Initialiser immédiatement et au montage
					splideInstance.on('mounted ready', updateProgress);
					splideInstance.on('move moved', updateProgress);

					// Force l'initialisation après un court délai
					requestAnimationFrame(() => {
						setTimeout(updateProgress, 50);
					});
				}

				// Masquer les arrows si tous les slides sont visibles
				const arrowsEl = element.querySelector('.splide__arrows');
				if (arrowsEl) {
					const toggleArrows = () => {
						const overflow = splideInstance.Components.Layout.isOverflow();
						arrowsEl.style.visibility = overflow ? '' : 'hidden';
					};
					splideInstance.on('mounted resize', toggleArrows);
				}

				// Collecter les sections
				const sectionElement = element.closest('section');
				if (sectionElement) {
					const sectionName = sectionElement.className || 'unnamed-section';
					if (!sectionsWithSplide.includes(sectionName)) {
						sectionsWithSplide.push(sectionName);
					}
				}

				initializedCount++;

			} catch (error) {
				window.log('error', 'SplideManager', `Initialization failed for element: ${element.className}`);

				// Initialiser avec config par défaut en cas d'erreur
				try {
					new Splide(element).mount();
					initializedCount++;
				} catch (fallbackError) {
					window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				}
			}
		});

		// Émettre un seul événement global avec la liste des sections
		if (initializedCount > 0) {
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		}
	},

	/**
	 * Pause l'autoplay d'un slider tant qu'il est hors ecran ou cache, le relance quand il redevient visible
	 */
	observeAutoplay(element) {
		if (!('IntersectionObserver' in window)) return;

		this._autoplayObserver ??= new IntersectionObserver((entries) => {
			entries.forEach(({ target, isIntersecting }) => {
				const autoplay = target._splide?.Components.Autoplay;
				if (!autoplay) return;
				isIntersecting ? autoplay.play() : autoplay.pause();
			});
		});

		this._autoplayObserver.observe(element);
	}
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		ReadMoreManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.DocumentHeightManager = DocumentHeightManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

window.addEventListener('resize', window.debounce(() => UIComponentsManager.onResize(), 150));

// Réinitialise les sliders non gérés par leur propre manager après un re-render AJAX du theme editor
document.addEventListener('shopify:section:load', () => SplideManager.init());
